import { useEffect, useState } from 'react';
import { performanceCycleApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { 
  Loader2, Calendar, Target, FileText, Award, UserCheck, 
  CheckCircle, Clock, Plus, Edit, Save, Lock, Unlock, Trash2
} from 'lucide-react';

const stageConfig = [
  { key: 'goal_setting', label: 'Goal Setting', icon: Target, description: 'Employees set their annual goals' },
  { key: 'mid_year', label: 'Mid-Year Review', icon: FileText, description: 'Mid-year progress review' },
  { key: 'year_end', label: 'Year-End Review', icon: Award, description: 'Final performance evaluation' },
  { key: 'calibration', label: 'Calibration', icon: UserCheck, description: 'HR calibrates final ratings' },
];

export default function PerformanceCyclePage() {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState({
    year: currentYear,
    goal_setting_start: `${currentYear}-01-01`,
    mid_year_start: `${currentYear}-07-01`,
    year_end_start: `${currentYear + 1}-01-01`,
    calibration_start: `${currentYear + 1}-02-01`,
    is_year_active: false,
    goal_setting_enabled: true,
    mid_year_enabled: true,
    year_end_enabled: true,
    calibration_enabled: true,
  });

  useEffect(() => {
    loadCycles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCycles = async () => {
    try {
      const response = await performanceCycleApi.getAll();
      setCycles(response.data);
      
      if (response.data.length === 0) {
        const currentCycle = await performanceCycleApi.get(currentYear);
        if (currentCycle.data) {
          setCycles([currentCycle.data]);
        }
      }
    } catch (error) {
      console.error('Failed to load cycles:', error);
      toast.error('Failed to load performance cycles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingCycle) {
        await performanceCycleApi.update(editingCycle.year, formData);
        toast.success('Performance cycle updated');
      } else {
        await performanceCycleApi.create(formData);
        toast.success('Performance cycle created');
      }
      setDialogOpen(false);
      resetForm();
      loadCycles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (cycle) => {
    try {
      await performanceCycleApi.update(cycle.year, { is_year_active: !cycle.is_year_active });
      toast.success(cycle.is_year_active ? 'Year disabled' : 'Year enabled');
      loadCycles();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleToggleStage = async (cycle, stageKey) => {
    try {
      const enabledKey = `${stageKey}_enabled`;
      const currentValue = cycle[enabledKey] !== false; // Default to true if not set
      await performanceCycleApi.update(cycle.year, { [enabledKey]: !currentValue });
      toast.success(!currentValue ? `${stageKey.replace('_', ' ')} stage enabled` : `${stageKey.replace('_', ' ')} stage locked`);
      loadCycles();
    } catch (error) {
      toast.error('Failed to update stage status');
    }
  };

  const handleEdit = (cycle) => {
    setEditingCycle(cycle);
    setFormData({
      year: cycle.year,
      goal_setting_start: cycle.goal_setting_start,
      mid_year_start: cycle.mid_year_start,
      year_end_start: cycle.year_end_start,
      calibration_start: cycle.calibration_start,
      is_year_active: cycle.is_year_active || false,
      goal_setting_enabled: cycle.goal_setting_enabled !== false,
      mid_year_enabled: cycle.mid_year_enabled !== false,
      year_end_enabled: cycle.year_end_enabled !== false,
      calibration_enabled: cycle.calibration_enabled !== false,
    });
    setDialogOpen(true);
  };

  const handleAddNew = () => {
    const existingYears = cycles.map(c => c.year);
    let newYear = currentYear;
    while (existingYears.includes(newYear)) {
      newYear++;
    }
    
    setEditingCycle(null);
    setFormData({
      year: newYear,
      goal_setting_start: `${newYear}-01-01`,
      mid_year_start: `${newYear}-07-01`,
      year_end_start: `${newYear + 1}-01-01`,
      calibration_start: `${newYear + 1}-02-01`,
      is_year_active: false,
      goal_setting_enabled: true,
      mid_year_enabled: true,
      year_end_enabled: true,
      calibration_enabled: true,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCycle(null);
    setFormData({
      year: currentYear,
      goal_setting_start: `${currentYear}-01-01`,
      mid_year_start: `${currentYear}-07-01`,
      year_end_start: `${currentYear + 1}-01-01`,
      calibration_start: `${currentYear + 1}-02-01`,
      is_year_active: false,
      goal_setting_enabled: true,
      mid_year_enabled: true,
      year_end_enabled: true,
      calibration_enabled: true,
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="performance-cycle-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Performance Cycle Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Add years and configure stage start dates. Enable a year to unlock it for employees.
          </p>
        </div>
        <Button onClick={handleAddNew} data-testid="add-cycle-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Year
        </Button>
      </div>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">How it works</p>
              <p className="text-sm text-blue-700">
                Add a year and set the start dates for each stage. Toggle Enable to make the year available for employees.
                Disabled years will show a lock icon in the My Goals page.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cycles List */}
      {cycles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Performance Cycles</h3>
            <p className="text-muted-foreground mb-4">Add a year to get started</p>
            <Button onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" /> Add Year
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {cycles.sort((a, b) => a.year - b.year).map((cycle) => (
            <Card key={cycle.year} className={`overflow-hidden ${cycle.is_year_active ? 'border-emerald-300' : 'border-gray-200'}`}>
              <CardHeader className={`py-4 ${cycle.is_year_active ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold ${cycle.is_year_active ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                      {cycle.year}
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {cycle.year} Performance Cycle
                        {cycle.is_year_active ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <Unlock className="h-3 w-3 mr-1" /> Enabled
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-gray-200 text-gray-600">
                            <Lock className="h-3 w-3 mr-1" /> Disabled
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {cycle.is_year_active ? 'Employees can set goals for this year' : 'Year is locked for employees'}
                      </CardDescription>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${cycle.year}`} className="text-sm">Enable</Label>
                      <Switch
                        id={`active-${cycle.year}`}
                        checked={cycle.is_year_active || false}
                        onCheckedChange={() => handleToggleActive(cycle)}
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(cycle)}>
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-4">
                <div className="grid grid-cols-4 gap-3">
                  {stageConfig.map((stage) => {
                    const Icon = stage.icon;
                    const stageData = cycle.stages?.[stage.key];
                    const isActive = stageData?.is_active;
                    const startDate = stageData?.start_date || cycle[`${stage.key}_start`];
                    const isStageEnabled = cycle[`${stage.key}_enabled`] !== false; // Default to true if not set
                    
                    return (
                      <div 
                        key={stage.key} 
                        className={`p-3 rounded-lg border ${!isStageEnabled ? 'border-red-300 bg-red-50' : isActive ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Icon className={`h-5 w-5 ${!isStageEnabled ? 'text-red-500' : isActive ? 'text-green-600' : 'text-gray-400'}`} />
                          <div className="flex items-center gap-1">
                            {!isStageEnabled ? (
                              <Badge className="bg-red-500 text-white text-xs">
                                <Lock className="h-3 w-3 mr-0.5" /> Locked
                              </Badge>
                            ) : isActive ? (
                              <Badge className="bg-green-500 text-white text-xs">Active</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Pending</Badge>
                            )}
                          </div>
                        </div>
                        <h4 className={`font-medium text-sm ${!isStageEnabled ? 'text-red-700' : isActive ? 'text-green-800' : 'text-gray-600'}`}>
                          {stage.label}
                        </h4>
                        <p className={`text-xs mt-1 ${!isStageEnabled ? 'text-red-500' : isActive ? 'text-green-600' : 'text-gray-500'}`}>
                          {formatDate(startDate)}
                        </p>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                          <span className="text-xs text-muted-foreground">Enable</span>
                          <Switch
                            checked={isStageEnabled}
                            onCheckedChange={() => handleToggleStage(cycle, stage.key)}
                            className="scale-75"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCycle ? 'Edit Performance Cycle' : 'Add Performance Cycle'}</DialogTitle>
            <DialogDescription>Set the start dates for each stage</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="year">Year</Label>
                <SearchableSelect
                  value={formData.year.toString()}
                  onValueChange={(value) => {
                    const yr = parseInt(value);
                    setFormData({
                      ...formData,
                      year: yr,
                      goal_setting_start: `${yr}-01-01`,
                      mid_year_start: `${yr}-07-01`,
                      year_end_start: `${yr + 1}-01-01`,
                      calibration_start: `${yr + 1}-02-01`,
                    });
                  }}
                  disabled={editingCycle !== null}
                  placeholder="Select year"
                  options={[currentYear - 1, currentYear, currentYear + 1, currentYear + 2, currentYear + 3].map(y => ({ value: y.toString(), label: y.toString() }))}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label>Enable Year</Label>
                  <p className="text-xs text-muted-foreground">Allow employees to set goals</p>
                </div>
                <Switch
                  checked={formData.is_year_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_year_active: checked })}
                />
              </div>

              {stageConfig.map((stage) => {
                const Icon = stage.icon;
                const fieldKey = `${stage.key}_start`;
                return (
                  <div key={stage.key} className="grid gap-2">
                    <Label className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      {stage.label} Start Date
                    </Label>
                    <Input
                      type="date"
                      value={formData[fieldKey]}
                      onChange={(e) => setFormData({ ...formData, [fieldKey]: e.target.value })}
                      required
                    />
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                {editingCycle ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
